import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bucket, GetSalesInsightsAggregatedRequest, SalesInsightsAggregate, useSalesInsightsAggregated } from '../../../../api/indicators';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { dateRange, formatDateParam } from '../../../../helpers/dateUtils';
import LineBarChart, { LineBarChartDataItem } from '../charts/lineBarChart/LineBarChart';
import { DashboardContext } from '../../DashboardContext';
import { SettingsContext } from '../../../../context/SettingsContext';
import { getBucketFromRange } from '../../dashboardHelpers';


const NetSalesLineBar = () => {
    const { t } = useTranslation();
    const { calendarPreferences } = useContext(SettingsContext);
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const [lineBarChartData, setLineBarChartData] = useState<LineBarChartDataItem[]>([]);
    const { refreshDate } = useContext(DashboardContext);


    const initialStartDate = useMemo(() => filters.periodFrom
        ? filters.periodFrom
        : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate, [filters.periodFrom]);

    const initialEndDate = useMemo(() => filters.periodTo
        ? filters.periodTo
        : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate, [filters.periodTo]);


    const [params, setParams] = useState<GetSalesInsightsAggregatedRequest>({
        fields: 'netSales,transactionVolume',
        startDate: formatDateParam(initialStartDate),
        endDate: formatDateParam(initialEndDate),
        locations: filters?.locations?.join(','),
        locationArea: filters?.locationArea?.id,
        businessStartHour: calendarPreferences.daysStart,
        bucket: getBucketFromRange(initialStartDate, initialEndDate)
    });

    const {
        data: salesInsightsAggregate,
        isLoading,
        isError,
        refetch: fetchSalesInsightsAggregate
    } = useSalesInsightsAggregated<SalesInsightsAggregate>(params, 'sales-aggregate');

    useEffect(() => {

        const start = filters.periodFrom
            ? filters.periodFrom
            : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate;

        const end = filters.periodTo
            ? filters.periodTo
            : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate;

        setParams({
            ...params,
            startDate: formatDateParam(start),
            endDate: formatDateParam(end),
            locations: filters?.locations?.join(','),
            locationArea: filters?.locationArea?.id,
            bucket: getBucketFromRange(start, end)
        });
    }, [filters]);

    useEffect(() => { fetchSalesInsightsAggregate(); }, [params]);

    useEffect(() => {
        if (salesInsightsAggregate) {
            setLineBarChartData(
                salesInsightsAggregate.salesInsightsAggregate?.map((salesInsightsAggregateItem) => ({
                    dateTime: new Date(salesInsightsAggregateItem.dateTime),
                    barData: salesInsightsAggregateItem?.salesInsights?.transactionVolume || 0,
                    line1: salesInsightsAggregateItem?.salesInsights?.netSalesIncTax || 0
                }))
            );
        }
    }, [salesInsightsAggregate]);

    useEffect(() => {
        if (refreshDate != null) {
            fetchSalesInsightsAggregate();
        }
    }, [refreshDate]);

    return (
        <LineBarChart
            title={t("screens.dashboarding.charts.netSales")}
            currency={salesInsightsAggregate?.currency}
            description={t("screens.dashboarding.charts.includingTax")}
            data={lineBarChartData}
            isLoading={isLoading}
            isError={isError}
            rightAxisLabel={t("screens.dashboarding.charts.volume")}
            leftAxisLabel={t("screens.dashboarding.charts.amount")}
            barLabel={t("screens.dashboarding.charts.volume")}
            lineLabels={[t("screens.troncReport.fields.totalAmount")]}
            bucket={params.bucket as Bucket}
            setBucket={(newView: Bucket) => setParams({ ...params, bucket: newView })}
            height={460}
        />
    )
}

export default NetSalesLineBar;
